import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, ListIcon } from 'lucide-react'
import { type PropsWithChildren, forwardRef, useState } from 'react'

import { Button } from '@/components/button/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { useResizeWindow } from '@/lib/hooks/use-resize-window'
import { type ClientPortalRelevantCaseItem } from '@/lib/queries/case-dashboard/types'
import { cn } from '@/lib/utils'

export interface CaseNavigationProps {
  currentCaseId: number
  proUserSelectedCases?: ClientPortalRelevantCaseItem[]
  onClickPreviousButton: VoidFunction
  onClickCase: (caseItem: ClientPortalRelevantCaseItem) => void
  onClickNextButton: VoidFunction
}

export const CaseNavigation = ({
  currentCaseId,
  proUserSelectedCases,
  onClickPreviousButton,
  onClickCase,
  onClickNextButton,
}: CaseNavigationProps) => {
  const [isOpenPopover, setIsOpenPopover] = useState(false)

  const { width: windowWidth } = useResizeWindow()

  const firstCase = proUserSelectedCases?.[0]
  const lastCase = proUserSelectedCases?.[proUserSelectedCases.length - 1]

  const isDisabledPreviousButton = !proUserSelectedCases || firstCase?.caseId === currentCaseId
  const isDisabledNextButton = !proUserSelectedCases || lastCase?.caseId === currentCaseId

  const openPopover = () => {
    setIsOpenPopover(true)
  }

  const closePopover = () => {
    setIsOpenPopover(false)
  }

  const handleChangePopoverOpen = (open: boolean) => {
    if (!open) {
      closePopover()
      return
    }

    openPopover()
  }

  return (
    <div className={cn('flex items-center justify-center space-x-2.5')}>
      <NavigationButton
        disabled={isDisabledPreviousButton}
        onClick={() => {
          onClickPreviousButton()
          closePopover()
        }}
      >
        <ChevronLeftIcon size={16} />
        <span>Previous</span>
      </NavigationButton>

      <Popover open={isOpenPopover} onOpenChange={handleChangePopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className={cn('flex space-x-2', 'text-primary-foreground')}
            onClick={openPopover}
          >
            <ListIcon size={16} />
            <span>List</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('flex flex-col', 'w-fit', 'px-1 py-2.5', {
            hidden: !isOpenPopover,
          })}
          align={windowWidth < 1024 ? 'center' : 'start'}
        >
          {!proUserSelectedCases && (
            <p className={cn('text-normal text-center text-sm text-foreground', 'px-3')}>No other cases to display.</p>
          )}
          {proUserSelectedCases?.map((caseItem) => (
            <Button
              key={caseItem.caseId}
              type="button"
              variant="ghost"
              size="sm"
              className={cn('flex justify-start')}
              onClick={() => {
                closePopover()
                onClickCase(caseItem)
              }}
            >
              <CheckIcon
                size={16}
                className={cn({
                  invisible: caseItem.caseId !== currentCaseId,
                  visible: caseItem.caseId === currentCaseId,
                })}
              />
              <span className={cn('ml-2')}>
                {caseItem.caseType} {caseItem.caseNumber}
              </span>
            </Button>
          ))}
        </PopoverContent>
      </Popover>

      <NavigationButton
        disabled={isDisabledNextButton}
        onClick={() => {
          onClickNextButton()
          closePopover()
        }}
      >
        <span>Next</span>
        <ChevronRightIcon size={16} />
      </NavigationButton>
    </div>
  )
}

type NavigationButtonProps = PropsWithChildren<{
  className?: string
  disabled?: boolean
  onClick?: VoidFunction
}>
const NavigationButton = forwardRef<HTMLButtonElement, NavigationButtonProps>(function NavigationButton(
  { children, className, disabled, onClick },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex items-center gap-x-2',
        'text-base font-normal text-primary-foreground',
        'px-4 py-2',
        {
          'text-zinc-400': disabled,
        },
        className
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
})
